/**
 * Командный раздел «Аккаунты» (team-portal/auth-and-roles) — только админ: создание сотрудников
 * Центрики и вкл/выкл/удаление аккаунтов. Список — реальный GET /api/team/accounts; поле `disabled`
 * приходит с бэкенда (Блок 2), дефолт-обход Блока 1 убран.
 *
 * team-access: раздел — он же справочник сотрудников (ФИО, телефон, почта), из которого выбирают
 * менеджера клиента и ответственного в заказе. Здесь же правятся ФИО, телефон и роль; свою запись
 * администратор не меняет.
 */
import { useEffect, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { Briefcase, UserPlus } from 'lucide-react'
import {
  teamApi,
  type CreateEmployeeInput,
  type TeamAccount,
  type TeamRole,
  type User,
} from '@/lib/api'
import { useAuth } from '@/app/auth-context'
import { TEAM_ROLE_LABEL, TEAM_ROLES } from '@/team/roles'
import { staffOptions } from '@/team/use-staff'
import { TrainingSubscriptionResponsibleCard } from '@/training/training-subscription-responsible'
import { getErrorMessage } from '@/lib/errors'
import { PageHead } from '@/design-system/components/layout/page-head'
import { Panel } from '@/design-system/components/layout/panel'
import { Card } from '@/design-system/components/layout/card'
import { Grid } from '@/design-system/components/layout/grid'
import { Button } from '@/design-system/components/primitives/button'
import { Field } from '@/design-system/components/primitives/field'
import { Input } from '@/design-system/components/primitives/input'
import { PasswordInput } from '@/design-system/components/primitives/password-input'
import { Alert } from '@/design-system/components/feedback/alert'
import { Badge } from '@/design-system/components/primitives/badge'
import { EmptyState } from '@/design-system/components/feedback/empty-state'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/components/primitives/select'
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeaderCell,
  DataTableRow,
} from '@/design-system/components/data/data-table'

/** Строка аккаунта: User с обязательным `disabled` (приходит с бэкенда, Блок 2). */
type AccountRow = TeamAccount

const ROLE_LABEL: Record<string, string> = {
  ...TEAM_ROLE_LABEL,
  company: 'Клиент-компания',
  individual: 'Физлицо',
}

const EMPTY_FORM: CreateEmployeeInput = {
  fullName: '',
  email: '',
  password: '',
  role: 'employee',
  phone: '',
}

/** Поле «Роль» — общее для создания и правки сотрудника. */
function RoleField({ value, onChange }: { value: TeamRole; onChange: (role: TeamRole) => void }) {
  return (
    <Field label="Роль" required className="my-0">
      {(props) => (
        <Select value={value} onValueChange={(v) => onChange(v as TeamRole)}>
          <SelectTrigger id={props.id} aria-required={props['aria-required']}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TEAM_ROLES.map((role) => (
              <SelectItem key={role} value={role}>
                {TEAM_ROLE_LABEL[role]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </Field>
  )
}

/** Значение пункта «Никому»: у Radix Select пустая строка зарезервирована. */
const NOBODY = '__nobody__'

/** «клиентов: 3 · заказов в работе: 5 · отменённых заказов: 1» — закрепления сотрудника; пусто, если их нет. */
export function assignmentsText(
  row: Pick<AccountRow, 'managedClients' | 'supportedClients' | 'openOrders' | 'cancelledOrders'>,
): string {
  const parts: string[] = []
  if (row.managedClients) parts.push(`клиентов: ${row.managedClients}`)
  // Клиенты на сопровождении (client-support-manager) переходят преемнику так же.
  if (row.supportedClients) parts.push(`на сопровождении: ${row.supportedClients}`)
  if (row.openOrders) parts.push(`заказов в работе: ${row.openOrders}`)
  // Отменённые заказы можно восстановить — при передаче дел они переходят преемнику
  // (order-cancel-restore).
  if (row.cancelledOrders) parts.push(`отменённых заказов: ${row.cancelledOrders}`)
  return parts.join(' · ')
}

/**
 * Передача дел сотрудника (team-visibility): его клиенты и незавершённые заказы переходят другому
 * действующему сотруднику либо остаются без менеджера и ответственного. Нужна перед отключением
 * учётной записи: сотрудники видят только закреплённое за ними.
 */
function TransferForm({
  account,
  staff,
  onDone,
  onCancel,
}: {
  account: AccountRow
  staff: AccountRow[]
  onDone: () => void
  onCancel: () => void
}) {
  const [to, setTo] = useState(NOBODY)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await teamApi.transferAssignments(account.id, to === NOBODY ? '' : to)
      onDone()
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось передать дела'))
      setSubmitting(false)
    }
  }

  return (
    <Card header={`Передать дела: ${account.fullName || account.email}`} className="mb-4">
      <form onSubmit={onSubmit} noValidate>
        {error ? (
          <Alert variant="danger" className="mb-4">
            {error}
          </Alert>
        ) : null}
        <p className="mb-3 text-txt-secondary">
          За сотрудником закреплено — {assignmentsText(account)}. Клиенты (и как менеджеру, и на
          сопровождении), незавершённые и отменённые заказы перейдут выбранному сотруднику; готовые
          заказы не меняются.
        </p>
        <Field label="Кому передать" className="my-0 max-w-narrow">
          {(props) => (
            <Select value={to} onValueChange={setTo}>
              <SelectTrigger id={props.id}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NOBODY}>
                  Никому — оставить без менеджеров и ответственного
                </SelectItem>
                {staffOptions(staff.filter((s) => s.id !== account.id && !s.disabled)).map((o) => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Field>
        <div className="mt-4 flex gap-2">
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Передача…' : 'Передать'}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={submitting}>
            Отмена
          </Button>
        </div>
      </form>
    </Card>
  )
}

/** Правка сотрудника: ФИО, телефон и роль. Почта входа и пароль здесь не меняются. */
function EditAccountForm({
  account,
  onSaved,
  onCancel,
}: {
  account: AccountRow
  onSaved: (next: AccountRow) => void
  onCancel: () => void
}) {
  const [fullName, setFullName] = useState(account.fullName)
  const [phone, setPhone] = useState(account.phone ?? '')
  const [role, setRole] = useState<TeamRole>(account.role as TeamRole)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    const input = { fullName: fullName.trim(), phone: phone.trim(), role }
    try {
      await teamApi.updateAccount(account.id, input)
      onSaved({ ...account, ...input })
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось сохранить изменения'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Card header={`Сотрудник: ${account.email}`} className="mb-4">
      <form onSubmit={onSubmit} noValidate>
        {error ? (
          <Alert variant="danger" className="mb-4">
            {error}
          </Alert>
        ) : null}
        <Grid cols={2}>
          <Field required label="ФИО" className="my-0">
            {(props) => (
              <Input
                {...props}
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
              />
            )}
          </Field>
          <Field label="Телефон" className="my-0">
            {(props) => (
              <Input
                {...props}
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            )}
          </Field>
          <RoleField value={role} onChange={setRole} />
        </Grid>
        <div className="mt-4 flex gap-2">
          <Button type="submit" disabled={submitting || !fullName.trim()}>
            {submitting ? 'Сохранение…' : 'Сохранить'}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel} disabled={submitting}>
            Отмена
          </Button>
        </div>
      </form>
    </Card>
  )
}

function CreateEmployeeForm({ onCreated }: { onCreated: (user: User) => void }) {
  const [form, setForm] = useState<CreateEmployeeInput>(EMPTY_FORM)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  function set<K extends keyof CreateEmployeeInput>(key: K, value: CreateEmployeeInput[K]) {
    setForm((f) => ({ ...f, [key]: value }))
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const { user } = await teamApi.createEmployee({ ...form, phone: form.phone?.trim() })
      onCreated(user)
      setForm(EMPTY_FORM)
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось создать сотрудника'))
    } finally {
      setSubmitting(false)
    }
  }

  const valid = form.fullName.trim() && form.email.trim() && form.password

  return (
    <Card header="Новый сотрудник Центрики" className="mb-4">
      <form onSubmit={onSubmit} noValidate>
        {error ? (
          <Alert variant="danger" className="mb-4">
            {error}
          </Alert>
        ) : null}
        <Grid cols={2}>
          <Field required label="ФИО" className="my-0">
            {(props) => (
              <Input {...props} value={form.fullName} onChange={(e) => set('fullName', e.target.value)} required />
            )}
          </Field>
          <Field required label="Электронная почта" className="my-0">
            {(props) => (
              <Input
                {...props}
                type="email"
                value={form.email}
                onChange={(e) => set('email', e.target.value)}
                required
              />
            )}
          </Field>
          <Field required label="Пароль" className="my-0">
            {(props) => (
              <PasswordInput
                {...props}
                autoComplete="new-password"
                value={form.password}
                onChange={(e) => set('password', e.target.value)}
                required
              />
            )}
          </Field>
          <Field label="Телефон" className="my-0">
            {(props) => (
              <Input
                {...props}
                type="tel"
                value={form.phone ?? ''}
                onChange={(e) => set('phone', e.target.value)}
              />
            )}
          </Field>
          <RoleField value={form.role} onChange={(role) => set('role', role)} />
        </Grid>
        <Button type="submit" className="mt-4" disabled={submitting || !valid}>
          {submitting ? 'Создание…' : 'Создать сотрудника'}
        </Button>
      </form>
    </Card>
  )
}

export function AccountsPage() {
  const navigate = useNavigate()
  const { context } = useAuth()
  const [rows, setRows] = useState<AccountRow[]>([])
  const [editing, setEditing] = useState<AccountRow | null>(null)
  const [transferring, setTransferring] = useState<AccountRow | null>(null)
  // reloadKey — перечитать список после передачи дел: счётчики меняются у двух сотрудников.
  const [reloadKey, setReloadKey] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    teamApi
      .listAccounts(undefined, controller.signal)
      .then((res) => {
        if (!controller.signal.aborted) setRows(res.items)
      })
      .catch((err) => {
        if (!controller.signal.aborted) setError(getErrorMessage(err, 'Не удалось загрузить аккаунты'))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [reloadKey])

  function onCreated(user: User) {
    setRows((prev) => [{ ...user, disabled: user.disabled ?? false }, ...prev])
    setShowForm(false)
  }

  async function toggleDisabled(row: AccountRow) {
    setActionError(null)
    const next = !row.disabled
    try {
      await teamApi.setAccountDisabled(row.id, next)
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, disabled: next } : r)))
    } catch (err) {
      setActionError(getErrorMessage(err, 'Не удалось изменить аккаунт'))
    }
  }

  async function remove(row: AccountRow) {
    setActionError(null)
    try {
      await teamApi.deleteAccount(row.id)
      setRows((prev) => prev.filter((r) => r.id !== row.id))
      setEditing((e) => (e?.id === row.id ? null : e))
      setTransferring((e) => (e?.id === row.id ? null : e))
    } catch (err) {
      setActionError(getErrorMessage(err, 'Не удалось удалить аккаунт'))
    }
  }

  return (
    <div>
      <PageHead
        title="Аккаунты"
        subtitle="Сотрудники Центрики — контакты, роли, включение/отключение, удаление"
        actions={
          <>
            <Button
              variant="secondary"
              leftIcon={<Briefcase className="size-4 stroke-current" aria-hidden />}
              onClick={() => navigate('/positions')}
            >
              Справочник должностей
            </Button>
            <Button
              leftIcon={<UserPlus className="size-4 stroke-current" aria-hidden />}
              onClick={() => setShowForm((s) => !s)}
            >
              Добавить сотрудника
            </Button>
          </>
        }
      />

      {showForm ? <CreateEmployeeForm onCreated={onCreated} /> : null}
      {editing ? (
        <EditAccountForm
          // Свой экземпляр на сотрудника: поля и ошибка не переходят к другой записи.
          key={editing.id}
          account={editing}
          onSaved={(next) => {
            setRows((prev) => prev.map((r) => (r.id === next.id ? next : r)))
            setEditing(null)
          }}
          onCancel={() => setEditing(null)}
        />
      ) : null}
      {transferring ? (
        <TransferForm
          key={transferring.id}
          account={transferring}
          staff={rows}
          onDone={() => {
            setTransferring(null)
            setReloadKey((k) => k + 1)
          }}
          onCancel={() => setTransferring(null)}
        />
      ) : null}
      {actionError ? (
        <Alert variant="danger" className="mb-4">
          {actionError}
        </Alert>
      ) : null}

      <TrainingSubscriptionResponsibleCard staff={rows.filter((r) => !r.disabled)} />

      <Panel>
        {error ? (
          <Alert variant="danger" className="m-4">
            {error}
          </Alert>
        ) : loading ? (
          <p className="px-4 py-10 text-center text-txt-secondary">Загрузка…</p>
        ) : rows.length === 0 ? (
          <EmptyState icon={UserPlus} title="Аккаунтов пока нет" description="Создайте первого сотрудника" />
        ) : (
          <DataTable>
            <DataTableHead>
              <DataTableRow>
                <DataTableHeaderCell>Сотрудник</DataTableHeaderCell>
                <DataTableHeaderCell>Телефон</DataTableHeaderCell>
                <DataTableHeaderCell>Роль</DataTableHeaderCell>
                <DataTableHeaderCell>Статус</DataTableHeaderCell>
                <DataTableHeaderCell className="text-right">Действия</DataTableHeaderCell>
              </DataTableRow>
            </DataTableHead>
            <DataTableBody>
              {rows.map((row) => (
                <DataTableRow key={row.id}>
                  <DataTableCell>
                    <div className="font-medium text-txt">{row.fullName}</div>
                    <div className="text-12 text-txt-secondary">{row.email}</div>
                    {/* Закрепления (team-visibility): чьи дела передать перед отключением. Передача —
                        ссылкой здесь же, чтобы колонка действий не росла в ширину. */}
                    {assignmentsText(row) ? (
                      <div className="text-12 text-txt-tertiary">
                        Закреплено — {assignmentsText(row)}.{' '}
                        <button
                          type="button"
                          className="rounded-7 text-accent underline-offset-2 outline-none hover:underline focus-visible:ring-2 focus-visible:ring-accent"
                          onClick={() => setTransferring(row)}
                        >
                          Передать дела
                        </button>
                      </div>
                    ) : null}
                  </DataTableCell>
                  <DataTableCell className="whitespace-nowrap text-txt-secondary">
                    {row.phone || '—'}
                  </DataTableCell>
                  <DataTableCell>{ROLE_LABEL[row.role] ?? row.role}</DataTableCell>
                  <DataTableCell>
                    {row.disabled ? (
                      <Badge variant="gray">Отключён</Badge>
                    ) : (
                      <Badge variant="green">Активен</Badge>
                    )}
                  </DataTableCell>
                  <DataTableCell className="text-right">
                    {/* flex-wrap: на узком окне кнопки переносятся, а не уезжают за край таблицы. */}
                    <div className="flex flex-wrap justify-end gap-2">
                      {/* Свою запись администратор не меняет (сервер отвечает отказом). */}
                      {row.id !== context?.user.id ? (
                        <Button size="sm" variant="secondary" onClick={() => setEditing(row)}>
                          Изменить
                        </Button>
                      ) : null}
                      <Button size="sm" variant="secondary" onClick={() => void toggleDisabled(row)}>
                        {row.disabled ? 'Включить' : 'Отключить'}
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => void remove(row)}>
                        Удалить
                      </Button>
                    </div>
                  </DataTableCell>
                </DataTableRow>
              ))}
            </DataTableBody>
          </DataTable>
        )}
      </Panel>
    </div>
  )
}
